import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, ArrowLeft, ChevronsRight, Columns } from '../../icons';
import { layerConfirm } from '../LayerPopup/LayerPopup';
import { useModalLeave } from '../../hooks/useModalLeave';
import { tMemo, getMemoLang, subscribeMemoLang } from '../../lib/memo-i18n';
import { memoContextOf } from '../../lib/memo-context';
import { MEMO_PANEL_ENABLED } from '../../lib/memo-flag';
import {
  useMemoPanelStore,
  MEMO_PANEL_MIN_WIDTH, MEMO_PANEL_MAX_WIDTH, MEMO_PANEL_DEFAULT_WIDTH,
} from '../../store/memoPanelStore';
import { useMemoStore } from '../../store/memoStore';
import { PaneResizer } from '../shared/PaneResizer';
import { MemoList } from './MemoList';
import { MemoEditor } from './MemoEditor';
import type { MemoDto } from '../../api/memos';
import './MemoPanel.css';

/**
 * MemoPanel (design-20260724-rightrail-memo Phase 1) — ⑤ 우측 레일 메모의 슬라이드 패널.
 *
 * **AppLayout 이 전역으로 마운트한다**(§4.3) — ⑤ 레일은 현재 캘린더만 opt-in 이라
 * 레일 버튼만 배선하면 캘린더에서만 열린다. 진입점(레일 버튼 · Ctrl/Cmd+Shift+M · 상태바)은
 * 모두 memoPanelStore.togglePanel 을 부르고, 패널 자체는 여기 하나뿐이다.
 *
 * 슬라이드(§4.4): 앱의 다른 바(NavBar · 캘린더 사이드바)와 **같은 폭 전이**,
 * --duration-base/--ease-standard. 열림·닫힘이 대칭이 되도록 마운트 직후 1 프레임은
 * 닫힘 상태로 두고(아래 entered), 이탈은 useModalLeave(200) 로 200ms 뒤 언마운트한다.
 */
export function MemoPanel() {
  useSyncExternalStore(subscribeMemoLang, getMemoLang, getMemoLang);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const open = useMemoPanelStore((s) => s.open);
  const view = useMemoPanelStore((s) => s.view);
  const dock = useMemoPanelStore((s) => s.dock);
  const width = useMemoPanelStore((s) => s.width);
  const contextFirst = useMemoPanelStore((s) => s.contextFirst);
  const closePanel = useMemoPanelStore((s) => s.closePanel);
  const togglePanel = useMemoPanelStore((s) => s.togglePanel);
  const showList = useMemoPanelStore((s) => s.showList);
  const showEditor = useMemoPanelStore((s) => s.showEditor);
  const toggleDock = useMemoPanelStore((s) => s.toggleDock);
  const setWidth = useMemoPanelStore((s) => s.setWidth);
  const toggleContextFirst = useMemoPanelStore((s) => s.toggleContextFirst);

  const contextMemos = useMemoStore((s) => s.contextMemos);
  const contextTotal = useMemoStore((s) => s.contextTotal);
  const memos = useMemoStore((s) => s.memos);
  const nextCursor = useMemoStore((s) => s.nextCursor);
  const loading = useMemoStore((s) => s.loading);
  const error = useMemoStore((s) => s.error);
  const draft = useMemoStore((s) => s.draft);
  const saveState = useMemoStore((s) => s.saveState);
  const savedAt = useMemoStore((s) => s.savedAt);
  const conflict = useMemoStore((s) => s.conflict);

  const [query, setQuery] = useState('');
  const { shouldRender, isLeaving } = useModalLeave(open, 200);

  // 열림 슬라이드 — 마운트하는 프레임에 곧바로 열림 클래스를 주면 **시작 상태가 없어**
  // transition 이 생략되고 "툭" 하고 나타난다(닫힐 때만 슬라이드되던 원인, 2026-07-24 리포트).
  // 닫힘(폭 0) 상태로 한 번 레이아웃을 확정한 뒤 열림 클래스를 붙여, 닫힐 때와 **같은**
  // 폭 전이로 들어오게 한다.
  const panelRef = useRef<HTMLElement | null>(null);
  const [entered, setEntered] = useState(false);
  useLayoutEffect(() => {
    if (!shouldRender || isLeaving) { setEntered(false); return; }
    // 강제 리플로우 — 브라우저가 닫힘 폭을 "이전 값"으로 기록하게 만든다.
    void panelRef.current?.getBoundingClientRect().width;
    setEntered(true);
  }, [shouldRender, isLeaving]);

  const ctx = memoContextOf(pathname);
  const activeContextKey = contextFirst ? ctx.contextKey : null;

  // 전역 단축키 Ctrl/Cmd+Shift+M — 입력 중에도 동작한다(§4.3).
  // 메모는 "입력하다 떠오른 것"을 적는 도구라 입력 필드 포커스를 이유로 막지 않는다.
  useEffect(() => {
    if (!MEMO_PANEL_ENABLED) return;
    function onKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'm' || e.key === 'M')) {
        e.preventDefault();
        togglePanel();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [togglePanel]);

  // 인라인 도킹 — 본문(app-shell)에 우측 여백을 만들어 패널과 나란히 놓는다.
  // 기존 레이아웃 DOM 을 건드리지 않기 위해 body 클래스 + CSS 변수로만 처리(additive).
  useEffect(() => {
    const inline = open && dock === 'inline';
    document.body.classList.toggle('memo-inline-docked', inline);
    if (inline) document.body.style.setProperty('--memo-panel-w', `${width}px`);
    return () => { document.body.classList.remove('memo-inline-docked'); };
  }, [open, dock, width]);

  const refresh = useCallback((q: string) => {
    void useMemoStore.getState().refresh({ query: q || undefined, contextKey: activeContextKey });
  }, [activeContextKey]);

  // 열릴 때 / 정렬 토글 / 화면 이동 시 목록 재조회.
  // ⚠ 편집 중에는 재정렬하지 않는다(§4.9) — 작성 중 목록이 흔들리면 안 된다.
  useEffect(() => {
    if (!open || view !== 'list') return;
    refresh(query);
    // query 는 아래 debounce effect 가 담당 — 여기서는 열림/컨텍스트/뷰 전환만 본다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, view, activeContextKey, refresh]);

  // 검색어 debounce.
  useEffect(() => {
    if (!open || view !== 'list') return;
    const id = setTimeout(() => refresh(query), 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // 라우트 전환 시 편집 중이면 즉시 저장(flush) — 미저장 유실 차단(§4.7).
  const prevPath = useRef(pathname);
  useEffect(() => {
    if (prevPath.current !== pathname) {
      prevPath.current = pathname;
      if (draft) void useMemoStore.getState().flush();
    }
  }, [pathname, draft]);

  const doClose = useCallback(() => {
    if (draft) void useMemoStore.getState().flush();
    closePanel();
  }, [draft, closePanel]);

  // Esc — 닫기(편집 중이면 flush 후).
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') doClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, doClose]);

  function openMemo(memo: MemoDto) {
    useMemoStore.getState().editExisting(memo);
    showEditor(memo.id);
  }

  function createMemo() {
    useMemoStore.getState().startDraft(ctx);
    showEditor(null);
  }

  function backToList() {
    void useMemoStore.getState().flush().then(() => {
      useMemoStore.getState().discardDraft();
      showList();
      refresh(query);
    });
  }

  async function removeMemo(memo: MemoDto) {
    if (!(await layerConfirm(tMemo('memo.confirm_delete')))) return;
    await useMemoStore.getState().remove(memo.id);
  }

  /**
   * 편집 화면에서 열어둔 메모 삭제 — 목록 카드 경로(removeMemo)와 같은 확인 절차를 쓴다.
   *
   * discardDraft() 를 remove() 보다 **먼저** 호출한다: discardDraft 가 대기 중인 자동저장
   * 타이머를 취소하므로, 삭제 직후 debounce 저장이 깨어나 이미 지운 메모를 PATCH 하는 것을 막는다.
   * 그래서 id 는 draft 를 버리기 전에 확보해 둔다.
   */
  async function removeOpenMemo() {
    const id = useMemoStore.getState().draft?.id;
    if (!id) return;
    if (!(await layerConfirm(tMemo('memo.confirm_delete')))) return;
    useMemoStore.getState().discardDraft();
    await useMemoStore.getState().remove(id);
    showList();
    refresh(query);
  }

  if (!MEMO_PANEL_ENABLED || !shouldRender) return null;

  return (
    <aside
      ref={panelRef}
      className={[
        'memo-panel',
        `memo-panel--${dock}`,
        // 마운트 직후 1 프레임(entered=false) 과 이탈 중에는 닫힘 상태 — CSS 의 `:not(.is-open)`.
        isLeaving ? 'is-leaving' : entered ? 'is-open' : '',
      ].filter(Boolean).join(' ')}
      style={{ ['--memo-panel-w' as string]: `${width}px` }}
      role="dialog"
      aria-modal="false"
      aria-label={tMemo('memo.title')}
    >
      <PaneResizer
        anchor="right"
        width={width}
        min={MEMO_PANEL_MIN_WIDTH}
        max={MEMO_PANEL_MAX_WIDTH}
        defaultWidth={MEMO_PANEL_DEFAULT_WIDTH}
        label={tMemo('memo.resize')}
        onResize={setWidth}
        baseClass="memo-resizer"
      />

      <div className="memo-head">
        {view === 'editor' ? (
          <button type="button" className="memo-iconbtn" onClick={backToList} title={tMemo('memo.back')} aria-label={tMemo('memo.back')}>
            <ArrowLeft size={14} />
          </button>
        ) : null}
        <span className="memo-head-title">{tMemo('memo.title')}</span>
        <span className="memo-head-spacer" />
        {view === 'list' && (
          <button type="button" className="memo-iconbtn is-primary" onClick={createMemo} title={tMemo('memo.new')} aria-label={tMemo('memo.new')}>
            <Plus size={14} />
          </button>
        )}
        <button
          type="button"
          className={`memo-iconbtn${dock === 'inline' ? ' is-primary' : ''}`}
          onClick={toggleDock}
          title={tMemo(dock === 'inline' ? 'memo.dock_overlay' : 'memo.dock_inline')}
          aria-label={tMemo(dock === 'inline' ? 'memo.dock_overlay' : 'memo.dock_inline')}
        >
          <Columns size={14} />
        </button>
        <button type="button" className="memo-iconbtn" onClick={doClose} title={tMemo('memo.close')} aria-label={tMemo('memo.close')}>
          <ChevronsRight size={14} />
        </button>
      </div>

      {view === 'list' || !draft ? (
        <MemoList
          contextMemos={contextMemos}
          contextTotal={contextTotal}
          contextLabel={ctx.contextLabel}
          memos={memos}
          query={query}
          loading={loading}
          error={error}
          hasMore={nextCursor !== null}
          contextFirst={contextFirst}
          onQueryChange={setQuery}
          onOpen={openMemo}
          onCreate={createMemo}
          onTogglePin={(m) => { void useMemoStore.getState().togglePin(m); }}
          onDelete={(m) => { void removeMemo(m); }}
          onExpandContext={() => { if (ctx.contextKey) void useMemoStore.getState().expandContextGroup(ctx.contextKey); }}
          onLoadMore={() => { void useMemoStore.getState().loadMore({ query: query || undefined, contextKey: activeContextKey }); }}
          onToggleContextFirst={toggleContextFirst}
          onGotoContext={(m) => { if (m.contextPath) navigate(m.contextPath); }}
          onRetry={() => refresh(query)}
        />
      ) : (
        <MemoEditor
          draft={draft}
          saveState={saveState}
          savedAt={savedAt}
          conflict={conflict}
          onChange={(patch) => useMemoStore.getState().setDraft(patch)}
          onResolveConflict={(mode) => { void useMemoStore.getState().resolveConflict(mode); }}
          onSaveAndClose={doClose}
          onDelete={() => { void removeOpenMemo(); }}
        />
      )}
    </aside>
  );
}
